import {
  cyan,
  cyanDark,
  green,
  greenDark,
  mauve,
  mauveDark,
  pink,
  pinkDark
} from '@radix-ui/colors'

import { HexValue, UI_BasePalette } from './types'
import { hexToHSL } from './utils'

const lightColors = {
  ui_neutral00: '#000000',
  ui_neutral01: '#FCFCFC',
  ui_neutral02: '#E6E6E6',
  ui_neutral03: '#B3B3B3',
  ui_neutral04: '#707070',
  ui_neutral05: '#5F5F5F',
  ui_neutral06: '#4A4A4A',
  ui_neutral07: '#1B1B1B',
  blue: '#1077B0',
  green: '#009D4B',
  red: '#D32121',
  violet: '#8041D0',
  yellow: '#B99D06',

  // 👇 shared with dark
  orange: '#EE8E57',
  pink: '#EC1118'
} satisfies Record<string, HexValue>

const darkColors = {
  ui_neutral00: '#FFFFFF',
  ui_neutral01: '#131316',
  ui_neutral02: '#222226',
  ui_neutral03: '#787C7A',
  ui_neutral04: '#55565C',
  ui_neutral05: '#A4A4A4',
  ui_neutral06: '#BFBFBF',
  ui_neutral07: '#FFFFFF',
  blue: '#7DD0FF',
  green: '#67C795',
  red: '#FC4747',
  violet: '#948AE3',
  yellow: '#FCE566',

  // 👇 shared with light
  orange: '#EE8E57',
  pink: '#F98286'
} satisfies Record<keyof typeof lightColors, HexValue>

export const colorsForEditor = {
  dark: {
    ...darkColors
  },
  light: {
    ...lightColors
  }
}

export const uiBaseColorsLight: UI_BasePalette = {
  ...cyan,
  ...green,
  ...mauve,
  ...pink,
  ui_neutral01: hexToHSL({ hex: lightColors.ui_neutral01 }),
  ui_neutral02: hexToHSL({ hex: lightColors.ui_neutral02 }),
  ui_neutral03: hexToHSL({ hex: lightColors.ui_neutral03 }),
  ui_neutral04: hexToHSL({ hex: lightColors.ui_neutral04 }),
  ui_neutral05: hexToHSL({ hex: lightColors.ui_neutral05 }),
  ui_neutral06: hexToHSL({ hex: lightColors.ui_neutral06 }),
  ui_neutral07: hexToHSL({ hex: lightColors.ui_neutral07 }),

  ui_blue: hexToHSL({ hex: lightColors.blue }),
  ui_blue_light: hexToHSL({ hex: lightColors.blue, alpha: 0.6 }),
  ui_blue_lightest: hexToHSL({ hex: lightColors.blue, alpha: 0.1 }),

  ui_green: hexToHSL({ hex: lightColors.green }),
  ui_green_light: hexToHSL({ hex: lightColors.green, alpha: 0.6 }),
  ui_green_lightest: hexToHSL({ hex: lightColors.green, alpha: 0.1 }),

  ui_orange: hexToHSL({ hex: lightColors.orange }),
  ui_orange_light: hexToHSL({ hex: lightColors.orange, alpha: 0.6 }),
  ui_orange_lightest: hexToHSL({ hex: lightColors.orange, alpha: 0.1 }),

  ui_pink: hexToHSL({ hex: lightColors.pink }),
  ui_pink_light: hexToHSL({ hex: lightColors.pink, alpha: 0.6 }),
  ui_pink_lightest: hexToHSL({ hex: lightColors.pink, alpha: 0.1 }),

  ui_red: hexToHSL({ hex: lightColors.red }),
  ui_red_light: hexToHSL({ hex: lightColors.red, alpha: 0.6 }),
  ui_red_lightest: hexToHSL({ hex: lightColors.red, alpha: 0.1 }),

  ui_violet: hexToHSL({ hex: lightColors.violet }),
  ui_violet_light: hexToHSL({ hex: lightColors.violet, alpha: 0.6 }),
  ui_violet_lightest: hexToHSL({ hex: lightColors.violet, alpha: 0.1 }),

  ui_yellow: hexToHSL({ hex: lightColors.yellow }),
  ui_yellow_light: hexToHSL({ hex: lightColors.yellow, alpha: 0.6 }),
  ui_yellow_lightest: hexToHSL({ hex: lightColors.yellow, alpha: 0.1 })
}

export const uiBaseColorsDark: UI_BasePalette = {
  ...cyanDark,
  ...greenDark,
  ...mauveDark,
  ...pinkDark,
  ui_neutral01: hexToHSL({ hex: '#131316' }),
  ui_neutral02: hexToHSL({ hex: '#222226' }),
  ui_neutral03: hexToHSL({ hex: '#363739' }),
  ui_neutral04: hexToHSL({ hex: '#55565C' }),
  ui_neutral05: hexToHSL({ hex: '#A4A4A4' }),
  ui_neutral06: hexToHSL({ hex: '#BFBFBF' }),
  ui_neutral07: hexToHSL({ hex: '#FFFFFF' }),

  ui_blue: hexToHSL({ hex: darkColors.blue }),
  ui_blue_light: hexToHSL({ hex: darkColors.blue, alpha: 0.6 }),
  ui_blue_lightest: hexToHSL({ hex: darkColors.blue, alpha: 0.1 }),

  ui_green: hexToHSL({ hex: darkColors.green }),
  ui_green_light: hexToHSL({ hex: darkColors.green, alpha: 0.6 }),
  ui_green_lightest: hexToHSL({ hex: darkColors.green, alpha: 0.1 }),

  ui_orange: hexToHSL({ hex: darkColors.orange }),
  ui_orange_light: hexToHSL({ hex: darkColors.orange, alpha: 0.6 }),
  ui_orange_lightest: hexToHSL({ hex: darkColors.orange, alpha: 0.1 }),

  ui_pink: hexToHSL({ hex: darkColors.pink }),
  ui_pink_light: hexToHSL({ hex: darkColors.pink, alpha: 0.6 }),
  ui_pink_lightest: hexToHSL({ hex: darkColors.pink, alpha: 0.1 }),

  ui_red: hexToHSL({ hex: darkColors.red }),
  ui_red_light: hexToHSL({ hex: darkColors.red, alpha: 0.6 }),
  ui_red_lightest: hexToHSL({ hex: darkColors.red, alpha: 0.1 }),

  ui_violet: hexToHSL({ hex: darkColors.violet }),
  ui_violet_light: hexToHSL({ hex: darkColors.violet, alpha: 0.6 }),
  ui_violet_lightest: hexToHSL({ hex: darkColors.violet, alpha: 0.1 }),

  ui_yellow: hexToHSL({ hex: darkColors.yellow }),
  ui_yellow_light: hexToHSL({ hex: darkColors.yellow, alpha: 0.6 }),
  ui_yellow_lightest: hexToHSL({ hex: darkColors.yellow, alpha: 0.1 })
}
